import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  criarTranscricao,
  entregarTranscricao,
} from '../../../../../services/TranscricaoBeneficiarioService';
import { formatDateTimeBrasilia } from '../../../../../utils/DateFormat';
import { gravarTranscricaoAnexos } from '../../transcricaoAnexoHelpers';
import { useTranscricao } from '../context';
import { TranscricaoStepEnum } from '../types';
import { isCodTipoTranscricaoPreenchido } from '../../utils/tipoAtendimentoUtils';
import { getTranscricaoErrorMessage } from '../../utils/transcricaoErrorUtils';

export const useNovaTranscricaoPage = () => {
  const { state, dispatch } = useTranscricao();
  const navigate = useNavigate();

  const [termosModalAberto, setTermosModalAberto] = useState(false);
  const [salvandoTranscricao, setSalvandoTranscricao] = useState(false);
  const [entregandoTranscricao, setEntregandoTranscricao] = useState(false);
  const [showModalFeedBack, setShowModalFeedBack] = useState(false);
  const [showModalSucesso, setShowModalSucesso] = useState(false);
  const [titleModal, setTitleModal] = useState('');
  const [subTitleModal, setSubTituloModal] = useState('');

  const canProceed = () => {
    if (state.currentStep === TranscricaoStepEnum.Anexos) {
      return state.anexos.length > 0 && !state.validandoAnexo;
    }
    return true;
  };

  const avancarStep = () => {
    dispatch({
      type: 'SET_STEP',
      payload: (state.currentStep + 1) as TranscricaoStepEnum,
    });
  };

  const openModalFeedBack = (title: string, subTitle: string) => {
    setTitleModal(title);
    setSubTituloModal(subTitle);
    setShowModalFeedBack(true);
  };

  const nextStep = (bloqueadoPorRascunho: boolean) => {
    if (bloqueadoPorRascunho) return;
    if (!canProceed()) return;

    if (
      state.currentStep === TranscricaoStepEnum.Anexos
      && !state.termosAceitos
    ) {
      setTermosModalAberto(true);
      return;
    }

    avancarStep();
  };

  const handleTermosConfirmados = async () => {
    if (salvandoTranscricao) return;

    // toISOString() grava UTC (~+3h no BR). Backend espera horário de Brasília.
    const dataAceite = formatDateTimeBrasilia();
    setSalvandoTranscricao(true);

    try {
      const resultado = await criarTranscricao({ dataAceite });

      if (!resultado.ok) {
        openModalFeedBack('Erro ao salvar solicitação', resultado.message);
        return;
      }

      const anexosGravados = await gravarTranscricaoAnexos(
        resultado.idTranscricao,
        state.anexos,
      );
      if (!anexosGravados.ok) {
        openModalFeedBack('Erro ao salvar anexos', anexosGravados.message);
        return;
      }

      dispatch({ type: 'SET_TERMOS_ACEITOS', payload: true });
      dispatch({ type: 'SET_DATA_ACEITE', payload: dataAceite });
      dispatch({ type: 'SET_ID_TRANSCRICAO', payload: resultado.idTranscricao });
      dispatch({ type: 'SET_ORIGEM_REVISAO', payload: 'aposSalvar' });
      dispatch({ type: 'SET_REVISAO_STATUS', payload: 'em_andamento' });

      setTermosModalAberto(false);
      avancarStep();
    } catch (error: unknown) {
      openModalFeedBack('Erro', getTranscricaoErrorMessage(error));
    } finally {
      setSalvandoTranscricao(false);
    }
  };

  const previousStep = () => {
    if (state.validandoAnexo) return;
    if (state.currentStep === TranscricaoStepEnum.Transcricao) return;

    if (state.currentStep === TranscricaoStepEnum.Anexos) {
      window.history.back();
      return;
    }
    dispatch({ type: 'SET_STEP', payload: (state.currentStep - 1) as TranscricaoStepEnum });
  };

  const podeVoltar =
    !state.validandoAnexo && state.currentStep !== TranscricaoStepEnum.Transcricao;

  const handleSolicitarAutorizacao = async () => {
    if (entregandoTranscricao) return;

    if (!state.idTranscricao) {
      openModalFeedBack(
        'Erro',
        'Não foi possível identificar a transcrição. Volte ao passo anterior e tente novamente.',
      );
      return;
    }

    if (!isCodTipoTranscricaoPreenchido(state.detalheTranscricao?.codTipoTranscricao)) {
      return;
    }

    setEntregandoTranscricao(true);

    try {
      const resultado = await entregarTranscricao({
        idTranscricao: state.idTranscricao,
        obs: state.detalheTranscricao?.observacao ?? '',
      });

      if (!resultado.ok) {
        openModalFeedBack('Erro ao solicitar autorização', resultado.message);
        return;
      }

      setShowModalSucesso(true);
    } catch (error: unknown) {
      openModalFeedBack('Erro', getTranscricaoErrorMessage(error));
    } finally {
      setEntregandoTranscricao(false);
    }
  };

  const handleEntendiSucesso = () => {
    setShowModalSucesso(false);
    navigate('/guiasEautorizacoes/transcricaoSolicitacao/consultar');
  };

  const revisaoPronta = state.revisaoStatus === 'pronta';

  const exibirSolicitarAutorizacao =
    state.currentStep === TranscricaoStepEnum.Transcricao && revisaoPronta;

  const solicitarAutorizacaoDisabled =
    entregandoTranscricao
    || !revisaoPronta
    || !isCodTipoTranscricaoPreenchido(state.detalheTranscricao?.codTipoTranscricao);

  return {
    state,
    termosModalAberto,
    setTermosModalAberto,
    salvandoTranscricao,
    entregandoTranscricao,
    showModalFeedBack,
    setShowModalFeedBack,
    showModalSucesso,
    titleModal,
    subTitleModal,
    canProceed,
    nextStep,
    previousStep,
    podeVoltar,
    handleTermosConfirmados,
    handleSolicitarAutorizacao,
    handleEntendiSucesso,
    exibirSolicitarAutorizacao,
    solicitarAutorizacaoDisabled,
  };
};
